import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FEDERAL_PAYCHECK_TAX_CONFIG,
  INCOME_TYPES,
} from '../config/federalPaycheckTaxConstants';
import {
  computeFullPaycheckWithholdingEstimate,
  computeTaxReadinessAnalysis,
} from '../lib/paycheckTaxEstimate';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import {
  trackCalculatorFinancialCheckupCtaClick,
  trackCalculatorSendResultCtaClick,
  trackLinkClick,
  trackOfferDetailClick,
} from '../lib/analytics';
import BRAND from '../config/brand';
import { CONTENT_CATEGORIES, OFFER_IDS, PRICING_MODELS } from '../config/planningOffers';
import ServiceNextStep from './ServiceNextStep';
import CalculatorPortalSignupCta from './CalculatorPortalSignupCta';
import CalculatorQandA from './CalculatorQandA';
import './IncomeChangePaycheckTaxEstimator.css';
import './RothTraditionalCalculator.css';

const PAY_FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly', periods: 52 },
  { value: 'biweekly', label: 'Bi-weekly', periods: 26 },
  { value: 'semimonthly', label: 'Semi-monthly', periods: 24 },
  { value: 'monthly', label: 'Monthly', periods: 12 },
];

const INCOME_TYPE_OPTIONS = [
  { value: INCOME_TYPES.W2, label: 'W-2 employee' },
  { value: INCOME_TYPES.SELF_EMPLOYED, label: 'Self-employed / 1099' },
  { value: INCOME_TYPES.MIXED, label: 'Both W-2 and self-employed / 1099' },
];

const PRIVACY_POLICY_URL = '/privacy-policy';
const PLANNING_MEETING_PATH = '/financial-planning-services/request-meeting';
const PLANNING_SERVICES_PATH = '/financial-planning-services#tax-planning-project';

const DEFAULT_COMPLEXITY = {
  rsus: false,
  rentalIncome: false,
  selfEmployment: false,
  itemizing: false,
  estimatedPayments: false,
};

const COMPLEXITY_TOGGLES = [
  { key: 'rsus', label: 'RSUs / equity compensation' },
  { key: 'rentalIncome', label: 'Rental income' },
  { key: 'itemizing', label: 'Itemized deductions' },
];

const DISCLAIMER_TOP = (
  <>
    <strong>Educational estimate only — not tax, legal, or investment advice.</strong> This check uses
    simplified assumptions (including the standard deduction only) and public tax parameters for tax year{' '}
    {FEDERAL_PAYCHECK_TAX_CONFIG.taxYear}. It is not a tax return and does not replace advice from a
    qualified CPA or financial professional.
  </>
);

function formatUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

/** Prefer two decimals when needed so 1.45% does not display as 1.5%. */
function formatPct(x, digits = 2) {
  if (x == null || !Number.isFinite(x)) return '—';
  const pct = x * 100;
  const fixed = Number.isInteger(pct) || Math.abs(pct - Math.round(pct)) < 1e-9
    ? pct.toFixed(0)
    : pct.toFixed(digits);
  return `${fixed}%`;
}

function remainingAmountForDisplay(estimate) {
  return estimate.householdIncomeType === INCOME_TYPES.W2
    ? estimate.remainingFitToCover
    : estimate.remainingAmountToCover;
}

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/** Allocate annual stated FIT by wage share, then per paycheck. */
function currentFitPerPaycheck(row, statedAnnualFit) {
  if (statedAnnualFit == null || !Number.isFinite(statedAnnualFit)) return null;
  const periods = Math.max(1, Number(row.payPeriodsPerYear) || 1);
  const share = Number.isFinite(row.wageShareOfHousehold) ? row.wageShareOfHousehold : 1;
  return roundMoney((statedAnnualFit * share) / periods);
}

function getPeriodsForFrequency(value) {
  const found = PAY_FREQUENCY_OPTIONS.find((o) => o.value === value);
  return found ? found.periods : 26;
}

function scheduleLabelFromPeriods(count) {
  const n = Number(count);
  const o = PAY_FREQUENCY_OPTIONS.find((x) => x.periods === n);
  return o ? `${o.label} (${o.periods}/year)` : `${n} periods/year`;
}

function parseMoneyInput(raw) {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function earnerNeedsW2(incomeType) {
  return incomeType === INCOME_TYPES.W2 || incomeType === INCOME_TYPES.MIXED;
}

function earnerNeedsSe(incomeType) {
  return incomeType === INCOME_TYPES.SELF_EMPLOYED || incomeType === INCOME_TYPES.MIXED;
}

function taxReadinessCategory(analysis) {
  if (!analysis) return undefined;
  if (analysis.withholdingComparison?.status === 'may_over') return 'withholding_surplus';
  if (analysis.withholdingComparison?.status === 'may_under') return 'withholding_gap';
  if (analysis.hasStatedWithholding) return 'withholding_on_track';
  if (analysis.complexity?.level === 'advanced') return 'high_complexity';
  return 'estimate_only';
}

function analyticsPayload(estimate, analysis) {
  return {
    result_category: taxReadinessCategory(analysis),
    income_type: estimate.householdIncomeType,
    has_self_employment_income: Boolean(estimate.hasSelfEmploymentIncome),
    filing_status: estimate.filingStatus,
    result_type: estimate.resultType || analysis.resultType,
  };
}

export default function IncomeChangePaycheckTaxEstimator() {
  const {
    trackComplete,
    trackFieldInteraction,
    trackReset,
    trackResultView,
  } = useCalculatorAnalytics({
    calculatorId: 'paycheck_tax_estimator',
    calculatorName: 'tax_projection',
  });

  const [filingStatus, setFilingStatus] = useState('single');
  const [primaryIncomeType, setPrimaryIncomeType] = useState(INCOME_TYPES.W2);
  const [spouseIncomeType, setSpouseIncomeType] = useState(INCOME_TYPES.W2);
  const [primaryAnnualW2, setPrimaryAnnualW2] = useState('');
  const [spouseAnnualW2, setSpouseAnnualW2] = useState('');
  const [bonusSupplemental, setBonusSupplemental] = useState('');
  const [primarySeGross, setPrimarySeGross] = useState('');
  const [primarySeExpenses, setPrimarySeExpenses] = useState('');
  const [spouseSeGross, setSpouseSeGross] = useState('');
  const [spouseSeExpenses, setSpouseSeExpenses] = useState('');
  const [annualWithholdingStated, setAnnualWithholdingStated] = useState('');
  const [estimatedPaymentsMade, setEstimatedPaymentsMade] = useState('');
  const [otherWithholding, setOtherWithholding] = useState('');
  const [primaryPayFrequency, setPrimaryPayFrequency] = useState('biweekly');
  const [spousePayFrequency, setSpousePayFrequency] = useState('biweekly');
  const [complexityFactors, setComplexityFactors] = useState(DEFAULT_COMPLEXITY);

  const isMarried = filingStatus === 'married_filing_jointly';
  const primaryPeriods = useMemo(() => getPeriodsForFrequency(primaryPayFrequency), [primaryPayFrequency]);
  const spousePeriods = useMemo(() => getPeriodsForFrequency(spousePayFrequency), [spousePayFrequency]);

  const primaryNeedsW2 = earnerNeedsW2(primaryIncomeType);
  const primaryNeedsSe = earnerNeedsSe(primaryIncomeType);
  const spouseNeedsW2 = isMarried && earnerNeedsW2(spouseIncomeType);
  const spouseNeedsSe = isMarried && earnerNeedsSe(spouseIncomeType);
  const householdHasSe = primaryNeedsSe || spouseNeedsSe;
  const householdHasW2 = primaryNeedsW2 || spouseNeedsW2;

  const estimate = useMemo(() => {
    const moneyOrZero = (raw, required) => {
      if (raw === '' || raw == null) return required ? null : 0;
      return parseMoneyInput(raw);
    };

    let pW2 = 0;
    let sW2 = 0;
    let bonus = 0;
    let pSeG = 0;
    let pSeE = 0;
    let sSeG = 0;
    let sSeE = 0;

    if (primaryNeedsW2) {
      pW2 = moneyOrZero(primaryAnnualW2, true);
      if (pW2 == null) return null;
      bonus = moneyOrZero(bonusSupplemental, false);
      if (bonusSupplemental !== '' && bonus == null) return null;
    }

    if (spouseNeedsW2) {
      sW2 = spouseAnnualW2 === '' ? 0 : moneyOrZero(spouseAnnualW2, false);
      if (sW2 == null) return null;
    }

    if (primaryNeedsSe) {
      pSeG = moneyOrZero(primarySeGross, true);
      if (pSeG == null) return null;
      pSeE = moneyOrZero(primarySeExpenses, false);
      if (primarySeExpenses !== '' && pSeE == null) return null;
    }

    if (spouseNeedsSe) {
      sSeG = moneyOrZero(spouseSeGross, true);
      if (sSeG == null) return null;
      sSeE = moneyOrZero(spouseSeExpenses, false);
      if (spouseSeExpenses !== '' && sSeE == null) return null;
    }

    if (!primaryNeedsW2 && !primaryNeedsSe) return null;
    if (isMarried && !spouseNeedsW2 && !spouseNeedsSe && spouseAnnualW2 === '' && spouseSeGross === '') {
      // Spouse income optional until entered; still allow primary-only MFJ estimate.
    }

    const withheld = moneyOrZero(annualWithholdingStated, false);
    if (annualWithholdingStated !== '' && withheld == null) return null;
    const estPay = moneyOrZero(estimatedPaymentsMade, false);
    if (estimatedPaymentsMade !== '' && estPay == null) return null;
    const other = moneyOrZero(otherWithholding, false);
    if (otherWithholding !== '' && other == null) return null;

    return computeFullPaycheckWithholdingEstimate({
      filingStatus,
      primaryIncomeType,
      spouseIncomeType: isMarried ? spouseIncomeType : INCOME_TYPES.W2,
      primaryAnnualW2: pW2,
      spouseAnnualW2: isMarried ? sW2 : 0,
      bonusSupplemental: bonus,
      primarySeGross: pSeG,
      primarySeExpenses: pSeE,
      spouseSeGross: isMarried ? sSeG : 0,
      spouseSeExpenses: isMarried ? sSeE : 0,
      federalIncomeTaxWithheld: withheld || 0,
      estimatedPaymentsMade: estPay || 0,
      otherWithholding: other || 0,
      primaryPayPeriodsPerYear: primaryPeriods,
      spousePayPeriodsPerYear: isMarried ? spousePeriods : undefined,
    });
  }, [
    filingStatus,
    isMarried,
    primaryIncomeType,
    spouseIncomeType,
    primaryNeedsW2,
    primaryNeedsSe,
    spouseNeedsW2,
    spouseNeedsSe,
    primaryAnnualW2,
    spouseAnnualW2,
    bonusSupplemental,
    primarySeGross,
    primarySeExpenses,
    spouseSeGross,
    spouseSeExpenses,
    annualWithholdingStated,
    estimatedPaymentsMade,
    otherWithholding,
    primaryPeriods,
    spousePeriods,
  ]);

  const analysis = useMemo(() => {
    if (!estimate) return null;
    const stated = annualWithholdingStated === '' ? null : parseMoneyInput(annualWithholdingStated);
    if (annualWithholdingStated !== '' && stated == null) return null;
    const flags = {
      ...complexityFactors,
      selfEmployment: householdHasSe || complexityFactors.selfEmployment,
      estimatedPayments: (parseMoneyInput(estimatedPaymentsMade) || 0) > 0 || complexityFactors.estimatedPayments,
    };
    return computeTaxReadinessAnalysis({
      estimate,
      complexityFlags: flags,
      statedWithholding: stated,
    });
  }, [estimate, complexityFactors, annualWithholdingStated, estimatedPaymentsMade, householdHasSe]);

  useEffect(() => {
    if (!estimate || !analysis) return;
    const payload = analyticsPayload(estimate, analysis);
    trackResultView(payload);
    trackComplete(payload);
  }, [estimate, analysis, trackComplete, trackResultView]);

  const updateField = (fieldName, setter) => (value) => {
    trackFieldInteraction(fieldName);
    setter(value);
  };

  const toggleComplexity = (key) => {
    trackFieldInteraction(`complexity_${key}`);
    setComplexityFactors((current) => ({ ...current, [key]: !current[key] }));
  };

  const resetCalculator = () => {
    setFilingStatus('single');
    setPrimaryIncomeType(INCOME_TYPES.W2);
    setSpouseIncomeType(INCOME_TYPES.W2);
    setPrimaryAnnualW2('');
    setSpouseAnnualW2('');
    setBonusSupplemental('');
    setPrimarySeGross('');
    setPrimarySeExpenses('');
    setSpouseSeGross('');
    setSpouseSeExpenses('');
    setAnnualWithholdingStated('');
    setEstimatedPaymentsMade('');
    setOtherWithholding('');
    setPrimaryPayFrequency('biweekly');
    setSpousePayFrequency('biweekly');
    setComplexityFactors(DEFAULT_COMPLEXITY);
    trackReset();
  };

  return (
    <div className="pete-page">
      <header className="pete-header">
        <div className="pete-header-inner">
          <Link className="pete-logo-link" to="/links">
            <img
              src={BRAND.logoSrc}
              alt="Bautista Planning and Analytics"
              className="pete-logo-img"
              width={320}
              height={64}
              decoding="async"
            />
          </Link>
        </div>
      </header>

      <div className="pete-body">
        <div className="pete-inner">
          <div className="pete-title-block trc-hero">
            <h1>Tax Readiness Check</h1>
            <p className="pete-tax-year trc-hero-sub">
              See if you&apos;re on track to owe, how much federal income tax to withhold, and what to change
              on your paycheck — for W-2, 1099, or both.
            </p>
            <p className="pete-social-calculator-note">
              Coming from TikTok, Reels, or Threads? Use this calculator, then compare your result to
              the decision you&apos;re actually trying to make.
            </p>
            <p className="trc-hero-scope">
              <strong>Tax year {FEDERAL_PAYCHECK_TAX_CONFIG.taxYear}.</strong>{' '}
              Simplified federal income-tax estimate using the standard deduction.{' '}
              <strong>Does not cover:</strong> State or local taxes, credits, or every return detail.
            </p>
          </div>

          <div className="pete-disclaimer-banner" role="note">
            {DISCLAIMER_TOP}
          </div>

          <CalculatorQandA
            title="Before you check your tax readiness"
            introduction="Three quick answers about what this estimate can—and cannot—tell you."
            calculatorId="paycheck_tax_estimator"
            calculatorName="tax_projection"
            location="calculator_intro"
            questions={[
              {
                id: 'tax_withholding_scope',
                question: 'What does this calculator estimate?',
                answer: <p>It estimates simplified federal income tax and compares that amount with the federal withholding and estimated payments you enter. It does not prepare a tax return.</p>,
              },
              {
                id: 'tax_paystub_line',
                question: 'Which withholding amount should I enter?',
                answer: <p>Use federal income tax withholding from your paystub or year-to-date payroll records. Do not combine it with Social Security, Medicare, state, or local withholding.</p>,
              },
              {
                id: 'tax_state_and_credits',
                question: 'Does the estimate include state taxes or tax credits?',
                answer: <p>No. It uses the standard deduction and simplified federal assumptions. State and local taxes, credits, itemized deductions, and many return-specific details are outside its scope.</p>,
              },
            ]}
          />

          <div className="pete-card">
            <h2>Household Information</h2>
            <p className="trc-intro-text">
              Start with how you earn income. This check estimates federal taxes using simplified assumptions for
              tax year {FEDERAL_PAYCHECK_TAX_CONFIG.taxYear}.
            </p>

            <section className="trc-section">
              <h3>Filing status</h3>
              <div className="rtc-toggle" role="group" aria-label="Filing status">
                <button
                  type="button"
                  className={`rtc-toggle-btn ${!isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={!isMarried}
                  onClick={() => updateField('filing_status', setFilingStatus)('single')}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`rtc-toggle-btn ${isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={isMarried}
                  onClick={() => updateField('filing_status', setFilingStatus)('married_filing_jointly')}
                >
                  Married filing jointly
                </button>
              </div>
            </section>

            <section className="trc-section">
              <h3>How do you earn income?</h3>
              <p className="trc-section-hint">
                {isMarried
                  ? 'Each spouse can select a different income type.'
                  : 'Choose the option that best matches your work.'}
              </p>
              <IncomeTypePicker
                id="pete-primary-income-type"
                label={isMarried ? 'Earner 1' : 'Your income'}
                value={primaryIncomeType}
                onChange={updateField('primary_income_type', setPrimaryIncomeType)}
              />
              {isMarried ? (
                <IncomeTypePicker
                  id="pete-spouse-income-type"
                  label="Earner 2"
                  value={spouseIncomeType}
                  onChange={updateField('spouse_income_type', setSpouseIncomeType)}
                />
              ) : null}
            </section>

            <section className="trc-section">
              <h3>Income &amp; payments</h3>
              <div className="trc-fields-grid">
                {primaryNeedsW2 ? (
                  <>
                    <Field
                      id="pete-primary-w2"
                      label={isMarried ? 'Earner 1 annual W-2 wages' : 'Annual W-2 wages'}
                      value={primaryAnnualW2}
                      onChange={updateField('primary_annual_w2', setPrimaryAnnualW2)}
                      placeholder="85000"
                    />
                    <Field
                      id="pete-bonus"
                      label="Bonus / supplemental wages (optional)"
                      value={bonusSupplemental}
                      onChange={updateField('bonus_supplemental', setBonusSupplemental)}
                      placeholder="0"
                      hint="Optional. Include annual bonus or supplemental pay."
                    />
                  </>
                ) : null}

                {spouseNeedsW2 ? (
                  <Field
                    id="pete-spouse-w2"
                    label="Earner 2 annual W-2 wages"
                    value={spouseAnnualW2}
                    onChange={updateField('spouse_annual_w2', setSpouseAnnualW2)}
                    placeholder="72000"
                  />
                ) : null}

                {primaryNeedsSe ? (
                  <>
                    <Field
                      id="pete-primary-se-gross"
                      label={isMarried ? 'Earner 1 gross business / 1099 income' : 'Gross business / 1099 income'}
                      value={primarySeGross}
                      onChange={updateField('primary_se_gross', setPrimarySeGross)}
                      placeholder="120000"
                      hint="Enter gross receipts — not the same as taxable profit."
                    />
                    <Field
                      id="pete-primary-se-expenses"
                      label={isMarried ? 'Earner 1 estimated deductible business expenses' : 'Estimated deductible business expenses'}
                      value={primarySeExpenses}
                      onChange={updateField('primary_se_expenses', setPrimarySeExpenses)}
                      placeholder="20000"
                      hint="Estimate only. Enter ordinary and necessary business expenses. Net profit = gross − expenses."
                    />
                  </>
                ) : null}

                {spouseNeedsSe ? (
                  <>
                    <Field
                      id="pete-spouse-se-gross"
                      label="Earner 2 gross business / 1099 income"
                      value={spouseSeGross}
                      onChange={updateField('spouse_se_gross', setSpouseSeGross)}
                      placeholder="80000"
                    />
                    <Field
                      id="pete-spouse-se-expenses"
                      label="Earner 2 estimated deductible business expenses"
                      value={spouseSeExpenses}
                      onChange={updateField('spouse_se_expenses', setSpouseSeExpenses)}
                      placeholder="15000"
                      hint="Estimate only. Ordinary and necessary business expenses."
                    />
                  </>
                ) : null}

                {householdHasW2 ? (
                  <Field
                    id="pete-withholding-stated"
                    label="Federal income tax already withheld from W-2 pay (optional)"
                    value={annualWithholdingStated}
                    onChange={updateField('annual_withholding_stated', setAnnualWithholdingStated)}
                    placeholder="From pay stubs or W-2"
                    hint="Enter the “Federal income tax” line total only — not Social Security or Medicare."
                  />
                ) : null}

                <Field
                  id="pete-estimated-payments"
                  label="Quarterly estimated federal tax payments already made (optional)"
                  value={estimatedPaymentsMade}
                  onChange={updateField('estimated_payments_made', setEstimatedPaymentsMade)}
                  placeholder="0"
                />

                <Field
                  id="pete-other-withholding"
                  label="Other federal income-tax withholding (optional)"
                  value={otherWithholding}
                  onChange={updateField('other_withholding', setOtherWithholding)}
                  placeholder="0"
                />

                {primaryNeedsW2 ? (
                  <div className="pete-field">
                    <label htmlFor="pete-pay-freq-primary">
                      {isMarried ? 'Earner 1 pay frequency' : 'Pay frequency'}
                    </label>
                    <select
                      id="pete-pay-freq-primary"
                      value={primaryPayFrequency}
                      onChange={(e) => updateField('primary_pay_frequency', setPrimaryPayFrequency)(e.target.value)}
                    >
                      {PAY_FREQUENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label} ({o.periods}/year)
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {spouseNeedsW2 ? (
                  <div className="pete-field">
                    <label htmlFor="pete-pay-freq-spouse">Earner 2 pay frequency</label>
                    <select
                      id="pete-pay-freq-spouse"
                      value={spousePayFrequency}
                      onChange={(e) => updateField('spouse_pay_frequency', setSpousePayFrequency)(e.target.value)}
                    >
                      {PAY_FREQUENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label} ({o.periods}/year)
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
              </div>
            </section>

            <section className="trc-section">
              <h3>What else applies to your situation?</h3>
              <p className="trc-section-hint">
                Optional — helps tailor the next-step conversation.
              </p>
              <div className="trc-complexity-grid">
                {COMPLEXITY_TOGGLES.map((item) => (
                  <label key={item.key} className="trc-complexity-item">
                    <input
                      type="checkbox"
                      checked={complexityFactors[item.key]}
                      onChange={() => toggleComplexity(item.key)}
                    />
                    <span>{item.label}</span>
                  </label>
                ))}
              </div>
            </section>

            <button className="pete-secondary-action" type="button" onClick={resetCalculator}>
              Reset calculator
            </button>
          </div>

          {estimate && analysis ? (
            <>
              <OnTrackHeroCard estimate={estimate} analysis={analysis} />

              <div className="pete-card">
                <FederalTaxEstimateCard
                  estimate={estimate}
                  itemizingSelected={complexityFactors.itemizing}
                />
              </div>

              <div className="pete-card">
                <RecommendedWithholdingCard
                  estimate={estimate}
                  analysis={analysis}
                  isMarried={isMarried}
                />
              </div>

              <WhatThisMeansCard hasSe={estimate.hasSelfEmploymentIncome} isMarried={isMarried} />

              <CalculatorQandA
                title="Questions to ask about your result"
                introduction="Use the estimate to identify the next item worth checking—not as a final filing answer."
                calculatorId="paycheck_tax_estimator"
                calculatorName="tax_projection"
                location="calculator_result"
                questions={[
                  {
                    id: 'tax_result_income_change',
                    question: 'What if my income changed during the year?',
                    answer: <p>Raises, bonuses, job changes, and uneven self-employment income can make annualized estimates less precise. Recalculate after major changes and compare against current year-to-date withholding.</p>,
                  },
                  {
                    id: 'tax_result_married_jobs',
                    question: 'Why can two-earner households still come up short?',
                    answer: <p>Each employer generally withholds based on the information provided for that job. Without coordinated W-4 elections, neither payroll system may account for the household&apos;s combined income.</p>,
                  },
                  {
                    id: 'tax_result_self_employment',
                    question: 'Why is 1099 revenue different from taxable income?',
                    answer: <p>Business revenue is reduced by eligible business expenses before arriving at net profit, and self-employment tax may also apply. The estimate depends heavily on the expense assumptions entered.</p>,
                  },
                ]}
                relatedLinks={[
                  { id: 'roth_analysis', label: 'Compare Roth and Traditional', to: '/roth-vs-traditional-calculator' },
                  { id: 'services_tax', label: 'Explore tax-planning services', to: '/financial-planning-services#tax-planning-project' },
                ]}
              />

              <CalculatorPortalSignupCta
                calculatorId="paycheck_tax_estimator"
                calculatorName="tax_projection"
                className="trc-portal-cta"
              />

              <PlannerHelpCta />
            </>
          ) : null}

          <div className="pete-disclaimer-banner pete-disclaimer-bottom" role="note">
            <h2 className="pete-disclaimer-bottom-title">Important disclosures</h2>
            <p className="pete-disclaimer-bottom-lead">{DISCLAIMER_TOP}</p>
            <p>
              This calculator provides a federal planning estimate for tax year {FEDERAL_PAYCHECK_TAX_CONFIG.taxYear}
              and does not calculate state or local taxes. Actual results may differ because of tax credits,
              deductions, benefit elections, business deductions, payroll treatment, and other tax-return information.
            </p>
            {householdHasSe ? (
              <p>
                1099 revenue is not the same as taxable profit. The estimate depends on the business expenses you enter.
              </p>
            ) : null}
            <p className="pete-disclaimer-bottom-links">
              <Link to={PRIVACY_POLICY_URL}>Privacy Policy</Link>
            </p>
          </div>

          <p className="pete-footer-note">
            <Link className="pete-portal-link" to="/links">
              Back to planning links
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function IncomeTypePicker({ id, label, value, onChange }) {
  return (
    <div className="pete-field trc-income-type-field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
      >
        {INCOME_TYPE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}


function OnTrackHeroCard({ estimate, analysis }) {
  const remaining = remainingAmountForDisplay(estimate);
  const alreadyCovered = roundMoney(
    (Number(estimate.federalIncomeTaxWithheld) || 0) + (Number(estimate.estimatedPaymentsMade) || 0),
  );
  const estimatedTax = estimate.householdIncomeType === INCOME_TYPES.W2
    ? estimate.federalIncomeTaxAnnual
    : estimate.planningSetAsideTarget;
  const hasWithholding = analysis.hasStatedWithholding;
  const comparison = analysis.withholdingComparison;
  const absRemaining = Math.abs(remaining);
  const isW2Only = estimate.householdIncomeType === INCOME_TYPES.W2;

  let variant = 'neutral';
  let headline = 'Enter withholding to see if you are on track';

  if (hasWithholding) {
    if (isW2Only && comparison) {
      if (comparison.status === 'may_under') {
        variant = 'owe';
        headline = `You may owe approximately ${formatUsd(absRemaining)}`;
      } else if (comparison.status === 'may_over') {
        variant = 'over';
        headline = `You may be overpaying by about ${formatUsd(Math.abs(comparison.gap) || absRemaining)}`;
      } else {
        variant = 'ontrack';
        headline = "You're on track";
      }
    } else if (remaining > 500) {
      variant = 'owe';
      headline = `You may owe approximately ${formatUsd(absRemaining)}`;
    } else if (remaining < -500) {
      variant = 'over';
      headline = `You may be overpaying by about ${formatUsd(absRemaining)}`;
    } else {
      variant = 'ontrack';
      headline = "You're on track";
    }
  } else if (remaining > 500) {
    variant = 'owe';
    headline = `About ${formatUsd(absRemaining)} still to cover`;
  } else if (remaining < -500) {
    variant = 'over';
    headline = `Payments exceed estimate by about ${formatUsd(absRemaining)}`;
  }

  const statusDot = variant === 'ontrack'
    ? '🟢'
    : variant === 'owe'
      ? '🟠'
      : variant === 'over'
        ? '🔵'
        : null;

  return (
    <div className={`pete-card trc-ontrack-hero trc-ontrack-hero--${variant}`} role="status">
      <p className="trc-ontrack-hero-eyebrow">Are you on track?</p>
      <h2 className="trc-ontrack-hero-headline">
        {statusDot ? <span aria-hidden="true">{statusDot} </span> : null}
        {headline}
      </h2>
      {!hasWithholding ? (
        <p className="trc-ontrack-hero-prompt">
          Enter your current federal income-tax withholding above to see whether you&apos;re on track.
        </p>
      ) : null}
      <p className="trc-ontrack-hero-why-label">Here&apos;s why:</p>
      <ul className="trc-ontrack-hero-why">
        <li>
          Estimated federal tax: <strong>{formatUsd(estimatedTax)}</strong>
        </li>
        <li>
          Already withheld (+ estimated payments): <strong>{formatUsd(alreadyCovered)}</strong>
          {!hasWithholding ? ' (no W-2 withholding entered)' : null}
        </li>
        <li>
          Remaining amount: <strong>{formatUsd(remaining)}</strong>
        </li>
      </ul>
    </div>
  );
}

function FederalTaxEstimateCard({ estimate, itemizingSelected }) {
  const summary = estimate.incomeSummary;
  const ladder = summary?.taxLadder;

  if (!ladder) return null;

  return (
    <>
      <TaxBracketSection ladder={ladder} />
      <p className="trc-income-summary-note">
        {itemizingSelected
          ? 'You indicated you itemize. This estimate still uses the standard deduction for simplicity.'
          : 'This estimate uses the standard deduction only. It is ordinary federal income tax before credits.'}
      </p>
    </>
  );
}

/**
 * Summary + applicable brackets only (lowest → highest).
 * Unreached brackets are filtered out of the DOM entirely.
 */
function TaxBracketSection({ ladder }) {
  const applicable = ladder.applicableSteps || [];
  const marginalPct = formatPct(ladder.marginalRate);
  const filingLabel = ladder.filingStatus === 'married_filing_jointly'
    ? 'Married filing jointly'
    : 'Single';

  return (
    <div className="trc-ladder">
      <h2 className="trc-ladder-section-title">
        Your {ladder.taxYear} federal income-tax estimate
      </h2>
      <p className="trc-ladder-filing">{filingLabel}</p>

      <div className="trc-estimate-summary" aria-label="Federal income-tax summary">
        <div className="trc-estimate-summary-primary">
          <span className="trc-estimate-summary-label">Estimated federal income tax</span>
          <span className="trc-estimate-summary-value trc-estimate-summary-value--primary">
            {formatUsd(ladder.totalFederalIncomeTax)}
          </span>
          <span className="trc-estimate-summary-sublabel">Before credits</span>
        </div>
        <div className="trc-estimate-summary-grid">
          <div className="trc-estimate-summary-item">
            <span className="trc-estimate-summary-label">Estimated taxable income</span>
            <span className="trc-estimate-summary-value">{formatUsd(ladder.taxableIncome)}</span>
          </div>
          <div className="trc-estimate-summary-item">
            <span className="trc-estimate-summary-label">Marginal federal tax bracket</span>
            <span className="trc-estimate-summary-value">{marginalPct}</span>
          </div>
          <div className="trc-estimate-summary-item">
            <span className="trc-estimate-summary-label">Effective federal tax rate</span>
            <span className="trc-estimate-summary-value">{formatPct(ladder.effectiveRateOnTaxable)}</span>
          </div>
        </div>
      </div>

      <p className="trc-ladder-lede">
        Your total tax is calculated by applying each tax rate only to the portion of taxable income
        inside that bracket.
      </p>
      <p className="trc-ladder-lede">
        Federal income tax is progressive. The first portion of your taxable income is taxed at the
        lowest rate, and only the dollars that reach higher brackets are taxed at those higher rates.
      </p>
      {applicable.length > 0 ? (
        <p className="trc-ladder-marginal-note">
          You are currently in the {marginalPct} marginal bracket, but your entire income is not taxed
          at {marginalPct}.
        </p>
      ) : null}

      {applicable.length > 0 ? (
        <div
          className="trc-ladder-stack"
          role="list"
          aria-label="Applicable federal tax brackets, lowest to highest"
        >
          {applicable.map((step) => {
            const fillPct = Math.round(Math.min(1, Math.max(0, step.fillRatio)) * 100);
            const rateLabel = formatPct(step.rate);
            const progressLabel = step.isTop
              ? `${rateLabel} bracket fully indicated (open-ended top bracket)`
              : `${rateLabel} bracket ${fillPct}% filled`;

            return (
              <div
                key={step.rate}
                role="listitem"
                className={`trc-ladder-step trc-ladder-step--${step.status}${step.isMarginal ? ' trc-ladder-step--marginal' : ''}`}
              >
                <div className="trc-ladder-step-head">
                  <h3 className="trc-ladder-step-title">
                    {rateLabel} bracket
                    {step.isMarginal ? (
                      <span className="trc-ladder-step-badge">Current marginal bracket</span>
                    ) : null}
                  </h3>
                  <p className="trc-ladder-step-range">{step.rangeLabel}</p>
                </div>

                <div
                  className="trc-ladder-step-bar"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={fillPct}
                  aria-label={progressLabel}
                >
                  <div
                    className="trc-ladder-step-fill"
                    style={{ width: `${fillPct}%` }}
                  />
                </div>

                <div className="trc-ladder-step-math">
                  <span className="trc-ladder-step-taxed">
                    {formatUsd(step.amountInBracket)} taxed at {rateLabel}
                  </span>
                  <span className="trc-ladder-step-tax">
                    Tax from this bracket: <strong>{formatUsd(step.taxInBracket)}</strong>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="trc-ladder-lede">No taxable income under these assumptions.</p>
      )}
    </div>
  );
}

function RecommendedWithholdingCard({ estimate, analysis, isMarried }) {
  const hasW2 = estimate.householdIncomeType !== INCOME_TYPES.SELF_EMPLOYED;
  const hasSe = estimate.hasSelfEmploymentIncome;
  const stated = analysis.hasStatedWithholding ? analysis.statedWithholding : null;
  const w2Earners = (estimate.perSpouse || []).filter((row) => row.hasW2);

  return (
    <>
      <h2>Recommended federal withholding</h2>
      {hasW2 && w2Earners.length > 0 ? (
        <div className="trc-recommend-earners">
          {w2Earners.map((row, index) => {
            const label = isMarried
              ? (index === 0 ? 'Primary paycheck' : 'Secondary paycheck')
              : 'Your paycheck';
            return (
              <RecommendedEarnerRow
                key={row.key}
                label={label}
                row={row}
                statedAnnualFit={stated}
              />
            );
          })}
        </div>
      ) : (
        <p className="trc-intro-text">
          No W-2 wages entered — use estimated payments below if you have self-employment income.
        </p>
      )}

      {hasSe ? (
        <div className="trc-set-aside-compact">
          <h3 className="trc-set-aside-compact-title">Estimated amount to set aside</h3>
          <p className="trc-set-aside-compact-intro">
            1099 income usually isn&apos;t withheld by the payer. Plan for the remaining federal amount with
            quarterly or monthly set-asides.
          </p>
          <div className="trc-estimate-grid trc-estimate-grid--two">
            <SnapshotCard
              label="Suggested quarterly set-aside"
              value={formatUsd(estimate.suggestedQuarterlySetAside)}
              accent
            />
            <SnapshotCard
              label="Suggested monthly set-aside"
              value={formatUsd(estimate.suggestedMonthlySetAside)}
            />
          </div>
        </div>
      ) : null}

      {isMarried && hasW2 && w2Earners.length > 1 ? (
        <p className="pete-allocation-note">
          Household federal income tax is split across paychecks by each earner&apos;s share of wages.
          Each employer only sees that job&apos;s W-4 — they don&apos;t know your household total.
        </p>
      ) : null}
    </>
  );
}

function RecommendedEarnerRow({ label, row, statedAnnualFit }) {
  const recommended = row.recommendedFederalIncomeTaxPerPaycheck
    ?? row.perPaycheck?.federalIncomeTax
    ?? null;
  const current = currentFitPerPaycheck(row, statedAnnualFit);
  const diff = current != null && recommended != null
    ? roundMoney(recommended - current)
    : null;

  let actionCopy = null;
  if (diff == null) {
    actionCopy = 'Enter current withholding above to compare.';
  } else if (Math.abs(diff) < 5) {
    actionCopy = 'About right';
  } else if (diff > 0) {
    actionCopy = `Increase withholding by ${formatUsd(diff)}/paycheck`;
  } else {
    actionCopy = `You're withholding about ${formatUsd(Math.abs(diff))} too much`;
  }

  return (
    <div className="trc-recommend-earner">
      <div className="trc-recommend-earner-head">
        <h3 className="trc-recommend-earner-label">{label}</h3>
        <p className="trc-recommend-earner-meta">{scheduleLabelFromPeriods(row.payPeriodsPerYear)}</p>
      </div>
      <dl className="trc-recommend-earner-lines">
        <div className="trc-recommend-earner-line">
          <dt>Recommended federal income tax per paycheck</dt>
          <dd>{formatUsd(recommended)}</dd>
        </div>
        <div className="trc-recommend-earner-line">
          <dt>Current withholding per paycheck</dt>
          <dd>{current == null ? '—' : formatUsd(current)}</dd>
        </div>
        <div className="trc-recommend-earner-line trc-recommend-earner-line--action">
          <dt>What to change</dt>
          <dd>{actionCopy}</dd>
        </div>
      </dl>
    </div>
  );
}

function WhatThisMeansCard({ hasSe, isMarried }) {
  return (
    <div className="pete-card">
      <h2>What this means</h2>
      <ul className="trc-means-list">
        <li>
          This is a simplified federal income-tax estimate using the standard deduction — not a full tax return.
        </li>
        <li>
          Match the recommended amount to the &ldquo;Federal income tax&rdquo; line on your paystub, not Social
          Security or Medicare.
        </li>
        {isMarried ? (
          <li>
            Two jobs or two earners often need coordinated W-4 elections so household withholding matches
            the joint return.
          </li>
        ) : (
          <li>
            Bonuses, a second job, or a mid-year raise can throw a single W-4 out of sync — revisit after
            big pay changes.
          </li>
        )}
        {hasSe ? (
          <li>
            Self-employment usually needs estimated payments; paycheck withholding alone rarely covers it.
          </li>
        ) : (
          <li>
            If you&apos;re short, increase FIT withholding for the rest of the year; if you&apos;re over,
            you can free up cash flow by reducing it.
          </li>
        )}
      </ul>
    </div>
  );
}

function PlannerHelpCta() {
  const financialCheckupButtonId = 'paycheck_tax_estimator_financial_checkup_cta';
  const sendResultButtonId = 'paycheck_tax_estimator_send_result_cta';
  const analyticsBase = {
    calculator_id: 'paycheck_tax_estimator',
    calculator_name: 'tax_projection',
    destination_type: 'contact_form',
  };
  const financialCheckupPath = `${PLANNING_MEETING_PATH}?offer=comprehensive_plan&cta=get_financial_plan&calculator_id=paycheck_tax_estimator&calculator_name=tax_projection`;
  const sendResultPath = `${PLANNING_MEETING_PATH}?cta=send_result&calculator_id=paycheck_tax_estimator&calculator_name=tax_projection`;

  const trackFinancialCheckup = () => {
    const payload = {
      ...analyticsBase,
      button_id: financialCheckupButtonId,
      button_label: 'Get a Financial Plan',
    };
    trackLinkClick(financialCheckupPath, 'Get a Financial Plan', payload);
    trackCalculatorFinancialCheckupCtaClick(payload);
  };

  const trackSendResult = () => {
    const payload = {
      ...analyticsBase,
      button_id: sendResultButtonId,
      button_label: "Send me your result and I'll point you in the right direction.",
    };
    trackLinkClick(sendResultPath, "Send me your result and I'll point you in the right direction.", payload);
    trackCalculatorSendResultCtaClick(payload);
  };

  return (
    <div className="pete-card trc-planner-cta">
      <h2>Want help interpreting your result?</h2>
      <p className="trc-intro-text">
        This check does not know about every detail behind your tax return, paycheck elections, business income,
        equity compensation, or household cash flow. Use it as a starting point, then compare the result to the
        decision you are actually trying to make.
      </p>

      <div className="trc-planner-options">
        <div className="trc-planner-option">
          <h3>Comprehensive Financial Plan</h3>
          <ul>
            <li>Interpret whether your tax result points to a withholding, estimated payment, or planning issue</li>
            <li>Connect the result to retirement contributions, cash flow, and major income changes</li>
            <li>Identify the next planning question worth solving</li>
          </ul>
          <Link
            className="pete-btn-primary pete-calculator-cta-button"
            to={financialCheckupPath}
            data-button-id={financialCheckupButtonId}
            onClick={trackFinancialCheckup}
          >
            Get a Financial Plan
          </Link>
        </div>

        <div className="trc-planner-option">
          <h3>Not ready for a call?</h3>
          <ul>
            <li>Use the contact form to send the result context</li>
            <li>Include the decision you are weighing right now</li>
            <li>Get pointed toward the planning next step that fits</li>
          </ul>
          <div className="pete-calculator-cta-secondary">
            <Link
              className="pete-calculator-cta-secondary-link"
              to={sendResultPath}
              data-button-id={sendResultButtonId}
              onClick={trackSendResult}
            >
              Send me your result and I&apos;ll point you in the right direction.
            </Link>
          </div>
          <p className="trc-planner-option-alt">
            Or discuss a{' '}
            <Link
              to={PLANNING_SERVICES_PATH}
              onClick={() => {
                const payload = {
                  ...analyticsBase,
                  button_id: 'paycheck_tax_estimator_tax_project_link',
                  button_label: 'comprehensive financial plan',
                  selected_offer: OFFER_IDS.COMPREHENSIVE,
                  offer_id: OFFER_IDS.COMPREHENSIVE,
                  pricing_model: PRICING_MODELS.FIXED,
                  content_category: CONTENT_CATEGORIES.TAX,
                  promotional_angle: 'tax_readiness_calculator',
                };
                trackLinkClick(PLANNING_SERVICES_PATH, 'comprehensive financial plan', payload);
                trackOfferDetailClick(payload);
              }}
            >
              comprehensive financial plan
            </Link>
            .
          </p>
        </div>
      </div>
      <ServiceNextStep
        contentCategory={CONTENT_CATEGORIES.TAX}
        calculatorId="paycheck_tax_estimator"
        calculatorName="tax_projection"
        promotionalAngle="tax_readiness_calculator"
      />
    </div>
  );
}

function Field({ id, label, value, onChange, placeholder, hint }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min="0"
        step="0.01"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? <p className="pete-hint">{hint}</p> : null}
    </div>
  );
}

function SnapshotCard({ label, value, subtitle, accent = false }) {
  return (
    <div className={`trc-snapshot-card${accent ? ' trc-snapshot-card--accent' : ''}`}>
      <div className="trc-snapshot-label">{label}</div>
      <div className="trc-snapshot-value">{value}</div>
      {subtitle ? <p className="trc-snapshot-sub">{subtitle}</p> : null}
    </div>
  );
}
